import {
  FetchProjectsTaskCacheKey,
  FetchProjectsTaskGqlQuery,
  ProjectsTaskNanoID,
  ProjectsTaskUUID
} from '../../projectsTasksTypes';
import { FetchCacheItemSources } from '../../../../types';

import { useCacheShowQuery } from '../../../common/reactQuery/hooks/useCacheShowQuery';

import { ProjectsTasksPermissions } from '../../projectsTasksConstants';

interface CacheProjectsTaskFields {
  uuid: ProjectsTaskUUID;
}

interface CacheProjectsTaskResponse<ItemType extends CacheProjectsTaskFields> {
  projectsTask: ItemType | null;
  isOptimistic?: boolean;
}

interface CacheProjectsTaskWithFullCacheKeyOptions {
  cacheKey?: never;
  fullCacheKey: FetchProjectsTaskCacheKey;
}

interface CacheProjectsTaskWithoutFullCacheKeyOptions {
  cacheKey: FetchProjectsTaskCacheKey;
  fullCacheKey?: never;
}

type CacheProjectsTaskOptions = {
  options?: {
    cacheTime?: number;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
  };
  uuid: ProjectsTaskNanoID | ProjectsTaskUUID;
  query: FetchProjectsTaskGqlQuery;
  source?: FetchCacheItemSources;
} & (
  | CacheProjectsTaskWithFullCacheKeyOptions
  | CacheProjectsTaskWithoutFullCacheKeyOptions
);

const itemKey = 'projectsTask';

function useCacheProjectsTask<ItemType extends CacheProjectsTaskFields>({
  cacheKey,
  fullCacheKey,
  options = {},
  uuid,
  query,
  source
}: CacheProjectsTaskOptions) {
  const {
    isOptimistic,
    item,
    itemError,
    itemErrorData,
    itemFetched,
    itemLoading,
    itemIsPlaceholderData,
    updateCacheShowQueryItemCache
  } = useCacheShowQuery<CacheProjectsTaskResponse<ItemType>, ItemType>({
    action: ProjectsTasksPermissions.READ_PROJECTS_TASK_SHOW_ES_QUERY,
    cacheKey,
    cacheQuery: query,
    fullCacheKey,
    itemKey,
    options,
    query,
    source,
    uuid,
    useCacheApiOnly: true
  });

  return {
    isOptimistic,
    projectsTask: item,
    projectsTaskErrorData: itemErrorData,
    projectsTaskError: itemError,
    projectsTaskFetched: itemFetched,
    projectsTaskLoading: itemLoading,
    projectsTaskIsPlaceholderData: itemIsPlaceholderData,
    updateProjectsTaskCache: updateCacheShowQueryItemCache
  };
}

export default useCacheProjectsTask;
